/**
 * Maps an auth failure to fixed Spanish copy, the same way mapOrderError does
 * for the order screens. The raw `err.message` is never rendered: it is written
 * in English, it is phrased for a developer, and on the login screen it is
 * deliberately uninformative (see below).
 *
 * `context` picks which screen is asking:
 *   - 'login'        — LoginPage.
 *   - 'requestReset' — RequestPasswordResetPage ("send me instructions").
 *   - 'reset'        — ResetPasswordPage (redeeming a link).
 */
import {getErrorCode} from './errorCode'

export type AuthErrorContext = 'login' | 'requestReset' | 'reset'

/**
 * One answer for every login failure. The backend collapses "no such user",
 * "wrong password", "user inactive" and "client inactive" into a single
 * `Invalid credentials` so that nobody can probe which emails exist; splitting
 * them apart here would hand that back.
 */
const LOGIN_FAILED =
  'No hemos podido iniciar sesión. Revisa tu email y tu contraseña; si el problema persiste, contacta con tu responsable de Wetaca.'

const RESET_LINK_UNUSABLE =
  'El enlace no es válido o ha caducado. Solicita uno nuevo para cambiar tu contraseña.'

const RESET_FAILED = 'No hemos podido cambiar la contraseña. Inténtalo de nuevo.'

const REQUEST_RESET_FAILED =
  'No hemos podido enviar las instrucciones. Inténtalo de nuevo en unos minutos.'

export const mapAuthError = (err: unknown, context: AuthErrorContext): string => {
  if (context === 'login') return LOGIN_FAILED
  if (context === 'requestReset') return REQUEST_RESET_FAILED

  // Redeeming a link: NOT_FOUND covers both an unknown/expired token and a
  // recovery whose user is gone — from here they are the same dead link.
  return getErrorCode(err) === 'NOT_FOUND' ? RESET_LINK_UNUSABLE : RESET_FAILED
}
